/**
 * Theme Provider
 *
 * React context provider for theme configuration
 */

import { createContext, useContext, useState, ComponentType, ReactNode } from 'react';
import { Theme } from '../types';
import { defaultTheme } from './default-theme';

/**
 * Theme context
 */
interface ThemeContextValue {
  /**
   * Current theme
   */
  theme: Theme;

  /**
   * Set theme
   */
  setTheme: (theme: Theme) => void;

  /**
   * Toggle between light and dark theme
   */
  toggleTheme: () => void;

  /**
   * Current theme mode
   */
  mode: 'light' | 'dark';

  /**
   * Set theme mode
   */
  setMode: (mode: 'light' | 'dark') => void;
}

/**
 * Theme context instance
 */
const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Theme provider props
 */
export interface ThemeProviderProps {
  /**
   * Initial theme
   */
  theme?: Theme;

  /**
   * Initial theme mode
   */
  mode?: 'light' | 'dark';

  /**
   * Children components
   */
  children: ReactNode;
}

/**
 * Theme Provider Component
 *
 * Provides theme context to child components
 */
export const ThemeProvider: ComponentType<ThemeProviderProps> = ({
  theme: initialTheme = defaultTheme,
  mode: initialMode = 'light',
  children,
}) => {
  const [theme, setTheme] = useState<Theme>(initialTheme);
  const [mode, setMode] = useState<'light' | 'dark'>(initialMode);

  const toggleTheme = () => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const value: ThemeContextValue = {
    theme,
    setTheme,
    toggleTheme,
    mode,
    setMode,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

/**
 * useTheme hook
 *
 * Access theme context
 *
 * @returns Theme context
 *
 * @example
 * ```tsx
 * const { theme, mode, toggleTheme } = useTheme();
 * ```
 */
export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }

  return context;
};
